/**
 * Local Carousel Ads — facility or promotional sponsor (`advertiser`): CareMap carousel with
 * geographic targeting groups (US ZCTAs / Census areas and/or Kenya counties or Spain / Thailand provinces in Firestore; zip listing sync is US-only today).
 * Facility: `facilities/{facilityId}/localCarousel/main` + syncLocalCarouselToZips.
 * Sponsor: `advertiserCarouselSponsorships/{uid}` + submit/approve callables.
 *
 * Geographic Targeting Groups: country, US ZCTA codes or Kenya counties, optional display name, ordered slides.
 */

import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  Link,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  Autocomplete,
} from "@mui/material";
import {
  Add as AddIcon,
  ArrowDownward,
  ArrowUpward,
  DeleteOutline,
  InfoOutlined,
  OpenInNew,
  Save as SaveIcon,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import firebase from "firebase/compat/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  isValidMapCarouselSlideId,
  validateGtgPricingFields,
  validateGtgCpmPricingFields,
  MIN_GTG_CPC_USD,
  MAX_GTG_CPC_USD,
  MIN_GTG_CPM_USD,
  MAX_GTG_CPM_USD,
  isYoutubeVideoUrl,
  canonicalYoutubeWatchUrlFromHref,
  extractYoutubeVideoId,
  GTG_TARGET_AUDIENCES,
  GTG_TARGET_AUDIENCE_LABELS,
  GTG_TYPE_STANDARD,
  GTG_TYPE_SYMPTOM_CHECKER,
  CANONICAL_SYMPTOM_CHECKER_PATH,
  normalizeGtgType,
  validateSymptomCheckerGtgConstraints,
  GTG_YOUTUBE_LINK_HELP_LINES,
  MSI_BLUE_TEXTURED_FILL_SX,
  MSI_BLUE_TEXTURED_HOVER_BG_SX,
  normalizeGtgTargetCountry,
  GTG_TARGET_COUNTRY_US,
  GTG_TARGET_COUNTRY_KE,
  GTG_TARGET_COUNTRY_ES,
  GTG_TARGET_COUNTRY_TH,
  GTG_TARGET_COUNTRY_LABELS,
  parseKenyaCountiesText,
  gtgTargetCountryUsesSubdivisionList,
  parseSubdivisionsForTargetCountry,
  orderedSubdivisionsFromSelectedSet,
  getGtgCanonicalSubdivisionList,
  gtgSubdivisionReferenceHint,
  gtgSubdivisionPluralNoun,
  validateGtgMaxCpcUsdInput,
  validateGtgMaxCpmUsdInput,
} from "@healthdesk/shared-utils";
import { db } from "@healthdesk/shared-services";
import { AuthContext } from "@healthdesk/shared-hooks";
import useMapCarouselStats from "../../hooks/useMapCarouselStats";
import { CarouselLogoSlotPreviewButton } from "../../components/map/CarouselLogoSlotPreviewButton";
import CareMapAdRateReference, {
  CAREMAP_HEALTHCARE_AD_RATE_REFERENCE_ANCHOR_ID,
} from "../../components/map/CareMapAdRateReference";
import PrepaidCarouselBudgetCard from "../../components/mapCarousel/PrepaidCarouselBudgetCard";
import ZipTargetingField from "../../components/mapCarousel/ZipTargetingField";
import ZipCollectionMapPicker from "../../components/mapCarousel/ZipCollectionMapPicker";
import CountyTargetingField from "../../components/mapCarousel/CountyTargetingField";
import KenyaCountyChecklistPicker from "../../components/mapCarousel/KenyaCountyChecklistPicker";
import KenyaCountyMapPicker from "../../components/mapCarousel/KenyaCountyMapPicker";
import GtgProvinceTargetingField from "../../components/mapCarousel/GtgProvinceTargetingField";
import GtgProvinceMapPicker from "../../components/mapCarousel/GtgProvinceMapPicker";
import GtgSubdivisionChecklistPicker from "../../components/mapCarousel/GtgSubdivisionChecklistPicker";
import { contentCardSx } from "../../styles/standaloneLayoutTokens";
import {
  LOCAL_CAROUSEL_DOC_ID,
  LOCAL_CAROUSEL_GENDER_LABELS,
  LOCAL_CAROUSEL_GENDERS,
  LOCAL_CAROUSEL_SUBCOLLECTION,
  normalizeLocalCarouselGender,
  isAllowedCarouselHref,
  openCarouselHrefInNewTab,
  formatCarouselStatsLine,
  MAP_CAROUSEL_LOGO_URL_HELPER_TEXT,
  ADVERTISER_CAROUSEL_SPONSORSHIP_COLLECTION,
  ADVERTISER_CAROUSEL_REVIEW_DRAFT,
  ADVERTISER_CAROUSEL_REVIEW_PENDING,
  ADVERTISER_CAROUSEL_REVIEW_REJECTED,
  ADVERTISER_CAROUSEL_REVIEW_APPROVED,
  ADVERTISER_CAROUSEL_REVIEW_LABELS,
  newGtgGroupId,
  loadGeographicTargetingGroupsFromDoc,
  geographicGroupToFirestorePayload,
  parseZipTextToUniqueZips,
  mapFirestoreSlideToRow,
  plainSponsorNameFromSlideMessage,
  MAP_CAROUSEL_ZIP_COLLECTIONS_SUBCOLLECTION,
} from "../../services/mapCarousel";

const SAVE_SUCCESS_SNACKBAR_MS = 6_000;

/** CPC/CPM “i” buttons: MSI textured fill; disabled matches contained-primary disabled. */
const GTG_INFO_ICON_BUTTON_SX = {
  ...MSI_BLUE_TEXTURED_FILL_SX,
  width: 22,
  height: 22,
  minWidth: 22,
  maxWidth: 22,
  maxHeight: 22,
  borderRadius: "50%",
  overflow: "hidden",
  color: "common.white",
  p: 0,
  flexShrink: 0,
  backgroundSize: "160% 100%",
  backgroundPosition: "center",
  "&:hover": {
    ...MSI_BLUE_TEXTURED_HOVER_BG_SX,
    color: "common.white",
  },
  "&.Mui-disabled": {
    backgroundColor: "rgba(27, 69, 132, 0.4)",
    backgroundImage: "none",
    color: "rgba(255,255,255,0.75)",
  },
};
/** Async save/sync errors — slightly longer so users can read Firebase messaging. */
const SAVE_FAILURE_SNACKBAR_MS = 9_000;

/**
 * User-facing text for Cloud Function / Firestore failures (Save & sync to map).
 * @param {unknown} err
 * @param {{ phase: 'firestore' | 'sync' }} ctx
 */
function formatLocalCarouselSaveFailureMessage(err, ctx) {
  const code = err && typeof err === "object" ? err.code : undefined;
  const raw =
    err && typeof err === "object" && typeof err.message === "string"
      ? err.message
      : "";
  const details =
    err && typeof err === "object" && typeof err.details === "string"
      ? err.details.trim()
      : "";

  if (ctx.phase === "firestore") {
    if (code === "permission-denied") {
      return "Could not save to Firestore (permission denied). Your facility account may not have access to this configuration.";
    }
    if (code === "unavailable" || raw.includes("offline")) {
      return "Could not reach Firestore. Check your connection and try Save & sync to map again.";
    }
    return raw && !String(raw).startsWith("FirebaseError:")
      ? raw
      : "Could not save your changes to Firestore. Nothing was updated; fix the issue and try again.";
  }

  if (code === "functions/unauthenticated") {
    return "Your session expired. Sign in again, then try Save & sync to map.";
  }
  if (code === "functions/permission-denied") {
    return "You do not have permission to sync map listings for this facility. Contact support if this is unexpected.";
  }
  if (code === "functions/not-found") {
    return "Sync could not find your saved configuration. Use Reload from server, then save again.";
  }
  if (code === "functions/invalid-argument") {
    return raw || "The server rejected the sync request. Check zips and slides, then try again.";
  }
  if (code === "functions/failed-precondition" && ctx.phase === "sync") {
    return raw && !String(raw).startsWith("FirebaseError:")
      ? raw
      : "Sync could not run (precondition failed). Check targeting and slides, then try again.";
  }
  if (code === "functions/internal" || code === "functions/unavailable") {
    const hint =
      raw && raw !== "internal" && raw !== "INTERNAL" && !/^firebase error/i.test(raw)
        ? ` (${raw})`
        : "";
    const detailSuffix = details ? ` ${details}` : "";
    return (
      "The sync step hit a server error" +
      hint +
      detailSuffix +
      ". Your draft is still saved in Firestore — try Save & sync again in a moment. " +
      "If this keeps happening, redeploy the sync function from the monorepo: " +
      "`firebase deploy --only functions:syncLocalCarouselToZips`."
    );
  }
  if (raw && !String(raw).startsWith("FirebaseError:")) {
    return raw;
  }
  return "Sync to map zip listings failed. Your draft may be saved in Firestore; use Reload from server to confirm. Check your connection and try again.";
}

const newSlideId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `s_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;

const emptySlideRow = () => ({
  id: newSlideId(),
  message: "",
  buttonLabel: "Learn more",
  href: "/",
  logoUrl: "",
  enabled: true,
  linkToVideo: false,
  learnMoreHref: "",
  patientSegments: { gender: "all" },
});

const emptyTargetingGroup = () => ({
  id: newGtgGroupId(),
  targetCountry: GTG_TARGET_COUNTRY_US,
  zipText: "",
  countyText: "",
  mapCarouselZipCollectionId: "",
  displayName: "",
  targetAudience: "patient",
  maxCpcUsd: MIN_GTG_CPC_USD,
  maxCpmUsd: MIN_GTG_CPM_USD,
  slides: [emptySlideRow()],
  gtgType: GTG_TYPE_STANDARD,
});

/** One slide, href fixed to standalone Symptom Checker; also shown on CareMap with full CTA. */
const emptySymptomCheckerTargetingGroup = () => {
  const slide = emptySlideRow();
  slide.href = CANONICAL_SYMPTOM_CHECKER_PATH;
  slide.buttonLabel = "Open Symptom Checker";
  return {
    id: newGtgGroupId(),
    targetCountry: GTG_TARGET_COUNTRY_US,
    zipText: "",
    countyText: "",
    mapCarouselZipCollectionId: "",
    displayName: "",
    targetAudience: "patient",
    maxCpcUsd: MIN_GTG_CPC_USD,
    maxCpmUsd: MIN_GTG_CPM_USD,
    slides: [slide],
    gtgType: GTG_TYPE_SYMPTOM_CHECKER,
  };
};

const emptyVideoSlideRow = () => ({
  ...emptySlideRow(),
  linkToVideo: true,
  href: "",
});

/**
 * @param {object[]} slides
 * @param {'full'|'video'|'direct'|'symptomChecker'} editorMode
 * @param {boolean} isSc
 * @returns {number[]}
 */
function getSlideIndicesForEditorMode(slides, editorMode, isSc) {
  const list = Array.isArray(slides) ? slides : [];
  if (editorMode === "symptomChecker") {
    return isSc ? list.map((_, i) => i) : [];
  }
  if (isSc || editorMode === "full") {
    return list.map((_, i) => i);
  }
  if (editorMode === "video") {
    return list.map((s, i) => (s.linkToVideo ? i : -1)).filter((i) => i >= 0);
  }
  if (editorMode === "direct") {
    return list.map((s, i) => (!s.linkToVideo ? i : -1)).filter((i) => i >= 0);
  }
  return list.map((_, i) => i);
}

/**
 * @param {object} g
 * @param {'full'|'video'|'direct'|'symptomChecker'} advertiserEditorMode
 */
function groupRequiresTargetingCollectionForFocusedAdvertiser(g, advertiserEditorMode) {
  if (
    advertiserEditorMode !== "video" &&
    advertiserEditorMode !== "direct" &&
    advertiserEditorMode !== "symptomChecker"
  ) {
    return false;
  }
  const isSc = normalizeGtgType(g.gtgType) === GTG_TYPE_SYMPTOM_CHECKER;
  return getSlideIndicesForEditorMode(g.slides || [], advertiserEditorMode, isSc).length > 0;
}

/**
 * @param {object[]} slides
 */
function cleanSlidesForPersist(slides) {
  return slides
    .map((s) => {
      const linkToVideo = Boolean(s.linkToVideo);
      const href = (s.href || "").trim();
      const learnMoreHref = (s.learnMoreHref || "").trim();
      const base = {
        id: (s.id || "").trim() || newSlideId(),
        message: (s.message || "").trim(),
        buttonLabel: (s.buttonLabel || "").trim() || "Learn more",
        logoUrl: (s.logoUrl || "").trim(),
        enabled: s.enabled !== false,
        patientSegments: {
          gender: normalizeLocalCarouselGender(s.patientSegments?.gender),
        },
      };
      if (linkToVideo) {
        const canon = canonicalYoutubeWatchUrlFromHref(href);
        if (!canon) {
          return null;
        }
        if (learnMoreHref && !isAllowedCarouselHref(learnMoreHref)) {
          return null;
        }
        return {
          ...base,
          linkToVideo: true,
          href: canon,
          learnMoreHref,
        };
      }
      if (extractYoutubeVideoId(href)) {
        return null;
      }
      if (!base.message || !base.buttonLabel || !href || !isAllowedCarouselHref(href)) {
        return null;
      }
      return {
        ...base,
        linkToVideo: false,
        href,
        learnMoreHref: "",
      };
    })
    .filter(Boolean);
}

/**
 * @param {{ id: string, zipText: string, displayName: string, slides: object[] }[]} uiGroups
 * @param {'advertiser'|'facility'} variant
 * @param {{ zipCollections?: { id: string, name: string, zipText: string, countyText: string, targetCountry: string }[], advertiserEditorMode?: 'full'|'video'|'direct'|'symptomChecker' } | null} [opts]
 * @returns {{ error?: string, geographicTargetingGroups?: object[] }}
 */
function buildGeographicTargetingPayload(uiGroups, variant, opts = null) {
  const zipCollections = opts?.zipCollections;
  const advertiserEditorMode = opts?.advertiserEditorMode ?? "full";
  const focusedAdvertiser =
    variant === "advertiser" &&
    (advertiserEditorMode === "video" ||
      advertiserEditorMode === "direct" ||
      advertiserEditorMode === "symptomChecker");

  const geographicTargetingGroups = [];
  for (const g of uiGroups) {
    const collectionId =
      typeof g.mapCarouselZipCollectionId === "string" ? g.mapCarouselZipCollectionId.trim() : "";

    if (
      focusedAdvertiser &&
      groupRequiresTargetingCollectionForFocusedAdvertiser(g, advertiserEditorMode) &&
      !collectionId
    ) {
      return {
        error:
          "Select a targeting collection for each geographic group on this page. Create and edit collections under Targeting collections.",
      };
    }

    let gForGeo = g;
    if (variant === "advertiser" && collectionId) {
      if (!Array.isArray(zipCollections)) {
        return {
          error:
            "Targeting collections could not be loaded. Refresh the page and try again.",
        };
      }
      const col = zipCollections.find((c) => c.id === collectionId);
      if (!col) {
        return {
          error:
            "A selected targeting collection no longer exists (it may have been deleted). Choose another collection under Targeting collections.",
        };
      }
      const colCountry = normalizeGtgTargetCountry(col.targetCountry);
      gForGeo = {
        ...g,
        targetCountry: colCountry,
        zipText: colCountry === GTG_TARGET_COUNTRY_US ? (col.zipText || "").trim() : "",
        countyText: colCountry === GTG_TARGET_COUNTRY_US ? "" : (col.countyText || "").trim(),
        mapCarouselZipCollectionId: collectionId,
      };
    }

    const isSc = normalizeGtgType(gForGeo.gtgType) === GTG_TYPE_SYMPTOM_CHECKER;
    const gGeo = isSc
      ? { ...gForGeo, targetCountry: GTG_TARGET_COUNTRY_US, countyText: "" }
      : gForGeo;
    const targetCountry = normalizeGtgTargetCountry(gGeo.targetCountry);
    const zipCodes =
      targetCountry === GTG_TARGET_COUNTRY_US
        ? parseZipTextToUniqueZips(gGeo.zipText)
        : [];
    const kenyaParse = gtgTargetCountryUsesSubdivisionList(targetCountry)
      ? parseSubdivisionsForTargetCountry(gGeo.countyText || "", targetCountry)
      : { counties: [], unknown: [] };

    let cleanedSlides = cleanSlidesForPersist(gForGeo.slides || []);
    if (isSc) {
      cleanedSlides = cleanedSlides.map((s) => ({
        ...s,
        href: CANONICAL_SYMPTOM_CHECKER_PATH,
      }));
    }

    const hasGeo =
      targetCountry === GTG_TARGET_COUNTRY_US
        ? zipCodes.length > 0
        : kenyaParse.counties.length > 0;

    if (kenyaParse.unknown.length > 0) {
      const sample = kenyaParse.unknown.slice(0, 5).join(", ");
      const more = kenyaParse.unknown.length > 5 ? "…" : "";
      const hint = gtgSubdivisionReferenceHint(targetCountry);
      return {
        error: `Unknown name(s): ${sample}${more}. Use exact names from the ${hint}.`,
      };
    }

    if (!hasGeo && cleanedSlides.length === 0) {
      continue;
    }
    if (!hasGeo || cleanedSlides.length === 0) {
      const geoHint =
        targetCountry === GTG_TARGET_COUNTRY_US
          ? "at least one valid 5-digit US ZCTA code"
          : `at least one valid ${targetCountry === GTG_TARGET_COUNTRY_KE ? "Kenya county" : "province"} (${gtgSubdivisionReferenceHint(targetCountry)})`;
      return {
        error: `Each geographic targeting group with content needs ${geoHint} and at least one valid slide (message, button, allowed link).`,
      };
    }
    const hasLinkSlide = cleanedSlides.some((s) => !s.linkToVideo);
    const hasVideoSlide = cleanedSlides.some((s) => s.linkToVideo);
    if (hasLinkSlide) {
      const pricing = validateGtgPricingFields({
        maxCpcUsd: gForGeo.maxCpcUsd,
        targetAudience: gForGeo.targetAudience,
      });
      if (!pricing.ok) {
        return { error: pricing.error };
      }
    }
    if (hasVideoSlide) {
      const cpm = validateGtgCpmPricingFields({
        maxCpmUsd: gForGeo.maxCpmUsd,
        targetAudience: gForGeo.targetAudience,
      });
      if (!cpm.ok) {
        return { error: cpm.error };
      }
    }
    geographicTargetingGroups.push(
      geographicGroupToFirestorePayload(
        { ...gGeo, slides: cleanedSlides },
        variant,
        (slides) => slides
      )
    );
  }
  if (geographicTargetingGroups.length === 0) {
    return {
      error:
        "Add at least one geographic targeting group with US ZCTA code(s) or Kenya counties and at least one valid slide.",
    };
  }
  const scVal = validateSymptomCheckerGtgConstraints(geographicTargetingGroups);
  if (!scVal.ok) {
    return { error: scVal.error };
  }
  return { geographicTargetingGroups };
}

/**
 * @param {object} group
 * @param {boolean} isSc Symptom Checker group (no “Link to video” mode).
 * @returns {{ hasVideoSlide: boolean, hasStandardSlide: boolean }}
 */
function gtgGroupSlideFormatFlags(group, isSc) {
  const slidesList = Array.isArray(group.slides) ? group.slides : [];
  const hasVideoSlide = !isSc && slidesList.some((s) => Boolean(s.linkToVideo));
  const hasStandardSlide = isSc || slidesList.some((s) => !s.linkToVideo);
  return { hasVideoSlide, hasStandardSlide };
}

/**
 * @param {{ isSc: boolean, hasVideoSlide: boolean, hasStandardSlide: boolean }} p
 */
function gtgMaxCpcFieldLabel({ isSc, hasVideoSlide, hasStandardSlide }) {
  if (isSc) {
    return "Max CPC (USD)";
  }
  if (!hasStandardSlide && hasVideoSlide) {
    return "Max CPC (USD) — unused (video-only slides)";
  }
  if (hasStandardSlide && hasVideoSlide) {
    return "Max CPC (USD) — standard-link slides";
  }
  return "Max CPC (USD)";
}

/**
 * @param {{ isSc: boolean, hasVideoSlide: boolean }} p
 */
function gtgMaxCpmFieldLabel({ isSc, hasVideoSlide }) {
  if (isSc) {
    return "Max CPM (USD) — not used (Symptom Checker)";
  }
  if (hasVideoSlide) {
    return "Max CPM (USD) — Link to video slides";
  }
  return "Max CPM (USD) — if you add Link to video";
}

/**
 * @param {{ variant?: "facility" | "advertiser", editorMode?: "full" | "video" | "direct" | "symptomChecker" }} props
 */
const LocalCarouselAdsPage = ({ variant = "facility", editorMode = "full" }) => {
  const { userData, user } = useContext(AuthContext);
  const { statsBySlideId } = useMapCarouselStats();
  const isAdvertiserVariant = variant === "advertiser";
  const effectiveEditorMode = isAdvertiserVariant ? editorMode : "full";
  const isFocusedEditor = isAdvertiserVariant && effectiveEditorMode !== "full";
  const gtgVariant = isAdvertiserVariant ? "advertiser" : "facility";
  const uid = user?.uid ? String(user.uid) : "";
  const facilityId = userData?.facilityId ? String(userData.facilityId).trim() : "";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [locationId, setLocationId] = useState("");
  const [groups, setGroups] = useState([emptyTargetingGroup()]);
  const [saveSuccessOpen, setSaveSuccessOpen] = useState(false);
  const [saveSuccessMessage, setSaveSuccessMessage] = useState("");
  const [saveFailureOpen, setSaveFailureOpen] = useState(false);
  const [saveFailureMessage, setSaveFailureMessage] = useState("");
  const [reviewStatus, setReviewStatus] = useState(ADVERTISER_CAROUSEL_REVIEW_DRAFT);
  const [reviewNote, setReviewNote] = useState("");
  const [submittingReview, setSubmittingReview] = useState(false);
  const [walletRefreshKey, setWalletRefreshKey] = useState(0);
  const [zipCollections, setZipCollections] = useState([]);
  const [zipApplyPick, setZipApplyPick] = useState(/** @type Record<string, string> */ ({}));
  const [healthcareRateRefExpanded, setHealthcareRateRefExpanded] = useState(false);
  const [pricingInfoModal, setPricingInfoModal] = useState(
    /** @type {"cpc" | "cpm" | null} */ (null)
  );

  const scrollToHealthcareAdRateReference = useCallback(() => {
    setHealthcareRateRefExpanded(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = document.getElementById(CAREMAP_HEALTHCARE_AD_RATE_REFERENCE_ANCHOR_ID);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    });
  }, []);

  const hasSymptomCheckerGtg = useMemo(
    () => groups.some((g) => normalizeGtgType(g.gtgType) === GTG_TYPE_SYMPTOM_CHECKER),
    [groups]
  );

  const formLocked =
    isAdvertiserVariant && reviewStatus === ADVERTISER_CAROUSEL_REVIEW_PENDING;

  const showAdvertiserTargetingCollectionOnly =
    isAdvertiserVariant &&
    (effectiveEditorMode === "video" ||
      effectiveEditorMode === "direct" ||
      effectiveEditorMode === "symptomChecker");

  const mainRef = useMemo(() => {
    if (isAdvertiserVariant) {
      if (!uid) return null;
      return db.collection(ADVERTISER_CAROUSEL_SPONSORSHIP_COLLECTION).doc(uid);
    }
    if (!facilityId) return null;
    return db
      .collection("facilities")
      .doc(facilityId)
      .collection(LOCAL_CAROUSEL_SUBCOLLECTION)
      .doc(LOCAL_CAROUSEL_DOC_ID);
  }, [isAdvertiserVariant, uid, facilityId]);

  const loadDoc = useCallback(async () => {
    if (!mainRef) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const snap = await mainRef.get();
      if (!snap.exists) {
        setLocationId("");
        setGroups([emptyTargetingGroup()]);
        if (isAdvertiserVariant) {
          setReviewStatus(ADVERTISER_CAROUSEL_REVIEW_DRAFT);
          setReviewNote("");
        }
        setLoading(false);
        return;
      }
      const data = snap.data();
      setLocationId(typeof data.locationId === "string" ? data.locationId : "");
      if (isAdvertiserVariant) {
        const rs = data.reviewStatus;
        setReviewStatus(
          rs === ADVERTISER_CAROUSEL_REVIEW_PENDING ||
            rs === ADVERTISER_CAROUSEL_REVIEW_REJECTED ||
            rs === ADVERTISER_CAROUSEL_REVIEW_APPROVED
            ? rs
            : ADVERTISER_CAROUSEL_REVIEW_DRAFT
        );
        setReviewNote(
          typeof data.reviewNote === "string" ? data.reviewNote.trim() : ""
        );
      } else {
        setReviewNote("");
      }
      const loaded = loadGeographicTargetingGroupsFromDoc(data, gtgVariant, (s) =>
        mapFirestoreSlideToRow(s, newSlideId)
      );
      setGroups(loaded.length > 0 ? loaded : [emptyTargetingGroup()]);
    } catch (e) {
      console.warn("LocalCarouselAdsPage: load failed", e);
      setError(e?.message || "Failed to load config");
      setGroups([emptyTargetingGroup()]);
    } finally {
      setLoading(false);
    }
  }, [mainRef, isAdvertiserVariant, gtgVariant]);

  useEffect(() => {
    loadDoc();
  }, [loadDoc]);

  useEffect(() => {
    if (!isAdvertiserVariant || !uid) {
      setZipCollections([]);
      return;
    }
    const ref = db
      .collection("users")
      .doc(uid)
      .collection(MAP_CAROUSEL_ZIP_COLLECTIONS_SUBCOLLECTION);
    // No orderBy: documents without `updatedAt` (legacy) must still appear in collection choosers.
    const unsub = ref.onSnapshot(
      (snap) => {
        const rows = [];
        snap.forEach((doc) => {
          const d = doc.data();
          const updatedAt = d.updatedAt;
          const ms =
            updatedAt && typeof updatedAt.toMillis === "function"
              ? updatedAt.toMillis()
              : 0;
          const name = typeof d.name === "string" ? d.name : "Untitled";
          rows.push({
            ms,
            row: {
              id: doc.id,
              name,
              zipText: typeof d.zipText === "string" ? d.zipText : "",
              countyText: typeof d.countyText === "string" ? d.countyText : "",
              targetCountry: normalizeGtgTargetCountry(d.targetCountry),
            },
          });
        });
        rows.sort((a, b) => {
          if (b.ms !== a.ms) return b.ms - a.ms;
          return String(a.row.name).localeCompare(String(b.row.name));
        });
        setZipCollections(rows.map((r) => r.row));
      },
      (e) => {
        console.warn("LocalCarouselAdsPage: zip collections listen failed", e);
      }
    );
    return () => unsub();
  }, [isAdvertiserVariant, uid]);

  const onTopUpReturn = useCallback((kind) => {
    if (kind === "success") {
      setSaveSuccessMessage(
        "Payment received. Your prepaid balance updates in a few seconds — try Save & sync again if listings were blocked for low balance."
      );
      setSaveSuccessOpen(true);
      setWalletRefreshKey((k) => k + 1);
    } else {
      setSaveFailureMessage("Checkout was canceled. No charge was made.");
      setSaveFailureOpen(true);
    }
  }, []);

  const addGroup = () => {
    setGroups((prev) => [...prev, emptyTargetingGroup()]);
  };

  const addSymptomCheckerGroup = () => {
    setGroups((prev) => [emptySymptomCheckerTargetingGroup(), ...prev]);
  };

  const removeGroup = (groupIndex) => {
    setGroups((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, i) => i !== groupIndex);
    });
  };

  const applyGroupTargetingCollection = (groupIndex, col) => {
    setGroups((prev) => {
      const next = [...prev];
      const cur = next[groupIndex];
      if (!cur) return prev;
      if (!col) {
        next[groupIndex] = {
          ...cur,
          mapCarouselZipCollectionId: "",
          zipText: "",
          countyText: "",
        };
        return next;
      }
      const country = normalizeGtgTargetCountry(col.targetCountry);
      next[groupIndex] = {
        ...cur,
        mapCarouselZipCollectionId: col.id,
        targetCountry: country,
        zipText: country === GTG_TARGET_COUNTRY_US ? (col.zipText || "").trim() : "",
        countyText: country === GTG_TARGET_COUNTRY_US ? "" : (col.countyText || "").trim(),
      };
      return next;
    });
  };

  const moveGroup = (groupIndex, delta) => {
    setGroups((prev) => {
      const j = groupIndex + delta;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[groupIndex], next[j]] = [next[j], next[groupIndex]];
      return next;
    });
  };

  const updateGroupField = (groupIndex, field, value) => {
    setGroups((prev) => {
      const next = [...prev];
      const cur = { ...next[groupIndex], [field]: value };
      if (field === "targetCountry") {
        if (value === GTG_TARGET_COUNTRY_US) {
          cur.countyText = "";
        } else {
          cur.zipText = "";
        }
      }
      next[groupIndex] = cur;
      return next;
    });
  };

  const toggleGroupZcta = useCallback((groupIndex, zipId) => {
    const z = String(zipId);
    setGroups((prev) => {
      const cur = prev[groupIndex];
      if (!cur) return prev;
      const current = parseZipTextToUniqueZips(cur.zipText || "");
      const idx = current.indexOf(z);
      const merged =
        idx >= 0 ? current.filter((x) => x !== z) : [...current, z];
      const next = [...prev];
      next[groupIndex] = { ...cur, zipText: merged.join("\n") };
      return next;
    });
  }, []);

  const toggleGroupSubdivision = useCallback((groupIndex, canon) => {
    const c = String(canon);
    setGroups((prev) => {
      const cur = prev[groupIndex];
      if (!cur) return prev;
      const tc = normalizeGtgTargetCountry(cur.targetCountry);
      const { counties } = parseSubdivisionsForTargetCountry(cur.countyText || "", tc);
      const nextSet = new Set(counties);
      if (nextSet.has(c)) nextSet.delete(c);
      else nextSet.add(c);
      const ordered = orderedSubdivisionsFromSelectedSet(nextSet, tc);
      const next = [...prev];
      next[groupIndex] = { ...cur, countyText: ordered.join("\n") };
      return next;
    });
  }, []);

  const moveSlide = (groupIndex, slideIndex, delta) => {
    setGroups((prev) => {
      const next = prev.map((g) => ({ ...g, slides: [...g.slides] }));
      const slides = next[groupIndex].slides;
      const j = slideIndex + delta;
      if (j < 0 || j >= slides.length) return prev;
      [slides[slideIndex], slides[j]] = [slides[j], slides[slideIndex]];
      return next;
    });
  };

  const removeSlide = (groupIndex, slideIndex) => {
    setGroups((prev) => {
      const next = prev.map((g, i) =>
        i === groupIndex
          ? { ...g, slides: g.slides.filter((_, si) => si !== slideIndex) }
          : g
      );
      if (next[groupIndex].slides.length === 0) {
        next[groupIndex] = {
          ...next[groupIndex],
          slides: [emptySlideRow()],
        };
      }
      return next;
    });
  };

  const addSlide = (groupIndex) => {
    setGroups((prev) => {
      const next = [...prev];
      next[groupIndex] = {
        ...next[groupIndex],
        slides: [...next[groupIndex].slides, emptySlideRow()],
      };
      return next;
    });
  };

  const addVideoSlide = (groupIndex) => {
    setGroups((prev) => {
      const next = [...prev];
      next[groupIndex] = {
        ...next[groupIndex],
        slides: [...next[groupIndex].slides, emptyVideoSlideRow()],
      };
      return next;
    });
  };

  const moveSlideForEditorMode = (groupIndex, subsetDisplayIndex, delta) => {
    setGroups((prev) => {
      const g = prev[groupIndex];
      const isScG = normalizeGtgType(g.gtgType) === GTG_TYPE_SYMPTOM_CHECKER;
      const idxs = getSlideIndicesForEditorMode(g.slides, effectiveEditorMode, isScG);
      const pos = subsetDisplayIndex + delta;
      if (pos < 0 || pos >= idxs.length) return prev;
      const a = idxs[subsetDisplayIndex];
      const b = idxs[pos];
      const next = prev.map((gr, gi) =>
        gi === groupIndex ? { ...gr, slides: [...gr.slides] } : gr
      );
      const slides = next[groupIndex].slides;
      [slides[a], slides[b]] = [slides[b], slides[a]];
      return next;
    });
  };

  /**
   * @param {number} groupIndex
   * @param {'merge'|'replace'} mode
   */
  const applyZipCollectionForGroup = (groupIndex, mode) => {
    const g = groups[groupIndex];
    if (!g) return;
    const colId = zipApplyPick[g.id];
    if (!colId) {
      setError("Choose a saved collection, then click Merge or Replace.");
      return;
    }
    const col = zipCollections.find((c) => c.id === colId);
    const groupCountry = normalizeGtgTargetCountry(g.targetCountry);
    if (!col || col.targetCountry !== groupCountry) {
      setError("That collection does not match this group’s country.");
      return;
    }

    if (groupCountry === GTG_TARGET_COUNTRY_US) {
      const colZips = parseZipTextToUniqueZips(col.zipText || "");
      if (colZips.length === 0) {
        setError("That collection has no valid 5-digit US ZCTA codes.");
        return;
      }
      setError(null);
      setGroups((prev) => {
        const cur = prev[groupIndex];
        if (!cur) return prev;
        const next = [...prev];
        const row = { ...cur };
        row.targetCountry = GTG_TARGET_COUNTRY_US;
        row.countyText = "";
        if (mode === "replace") {
          row.zipText = colZips.join("\n");
        } else {
          const merged = parseZipTextToUniqueZips(`${row.zipText || ""}\n${col.zipText || ""}`);
          row.zipText = merged.join("\n");
        }
        next[groupIndex] = row;
        return next;
      });
      return;
    }

    const { counties: colSubs, unknown: colUnknown } = parseSubdivisionsForTargetCountry(
      col.countyText || "",
      groupCountry
    );
    if (colSubs.length === 0 || colUnknown.length > 0) {
      setError(
        colUnknown.length
          ? "That collection contains unknown subdivision names; fix it under Targeting collections before applying."
          : "That collection has no valid subdivisions for this country."
      );
      return;
    }
    setError(null);
    setGroups((prev) => {
      const cur = prev[groupIndex];
      if (!cur) return prev;
      const next = [...prev];
      const row = { ...cur };
      row.targetCountry = groupCountry;
      row.zipText = "";
      const canonOrder = getGtgCanonicalSubdivisionList(groupCountry);
      if (mode === "replace") {
        row.countyText = colSubs.join("\n");
      } else {
        const mergedSet = new Set([
          ...parseSubdivisionsForTargetCountry(row.countyText || "", groupCountry).counties,
          ...colSubs,
        ]);
        row.countyText = canonOrder.filter((c) => mergedSet.has(c)).join("\n");
      }
      next[groupIndex] = row;
      return next;
    });
  };

  const updateSlide = (groupIndex, slideIndex, field, value) => {
    setGroups((prev) => {
      const next = prev.map((g) => ({ ...g, slides: g.slides.map((s) => ({ ...s })) }));
      const row = next[groupIndex].slides[slideIndex];
      if (field === "patientGender") {
        row.patientSegments = { ...row.patientSegments, gender: value };
      } else if (field === "href") {
        row.href = value;
        if (isYoutubeVideoUrl(value)) {
          row.linkToVideo = true;
        }
      } else {
        row[field] = value;
      }
      return next;
    });
  };

  const applyPersistedSlidesByGroupId = (payloadGroups) => {
    const idToPayload = new Map(payloadGroups.map((g) => [g.id, g]));
    setGroups((prev) =>
      prev.map((g) => {
        const p = idToPayload.get(g.id);
        if (!p || !Array.isArray(p.slides)) {
          return g;
        }
        return {
          ...g,
          slides: p.slides.map((s) => ({
            id: s.id,
            message: s.message || "",
            buttonLabel: s.buttonLabel || "Learn more",
            href: s.href || "",
            logoUrl: s.logoUrl || "",
            enabled: s.enabled !== false,
            linkToVideo: Boolean(s.linkToVideo),
            learnMoreHref:
              typeof s.learnMoreHref === "string" ? s.learnMoreHref.trim() : "",
            patientSegments: {
              gender: normalizeLocalCarouselGender(s.patientSegments?.gender),
            },
          })),
        };
      })
    );
  };

  const handleSaveAndSync = async () => {
    if (isAdvertiserVariant) {
      return;
    }
    if (!mainRef || !facilityId) {
      setError("Missing facility ID on your account.");
      return;
    }
    setSaving(true);
    setSyncing(true);
    setError(null);
    setSaveSuccessOpen(false);
    setSaveFailureOpen(false);
    setSaveFailureMessage("");
    const built = buildGeographicTargetingPayload(groups, "facility");
    if (built.error) {
      setError(built.error);
      setSaving(false);
      setSyncing(false);
      return;
    }
    try {
      await mainRef.set(
        {
          geographicTargetingGroups: built.geographicTargetingGroups,
          locationId: (locationId || "").trim(),
          updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
          zipCodes: firebase.firestore.FieldValue.delete(),
          slides: firebase.firestore.FieldValue.delete(),
          facilityDisplayName: firebase.firestore.FieldValue.delete(),
        },
        { merge: true }
      );
    } catch (e) {
      console.warn("LocalCarouselAdsPage: Firestore save failed", e);
      const message = formatLocalCarouselSaveFailureMessage(e, { phase: "firestore" });
      setSaveFailureMessage(message);
      setError(message);
      setSaveFailureOpen(true);
      setSaving(false);
      setSyncing(false);
      return;
    }

    applyPersistedSlidesByGroupId(built.geographicTargetingGroups);

    const hasGtgSubdivisionPublish = built.geographicTargetingGroups.some(
      (grp) =>
        gtgTargetCountryUsesSubdivisionList(grp.targetCountry) &&
        Array.isArray(grp.kenyaCounties) &&
        grp.kenyaCounties.length > 0
    );
    if (hasGtgSubdivisionPublish) {
      setSaveSuccessMessage(
        "Saved to Firestore. Kenya / Spain / Thailand subdivision targeting is stored but not published to CareMap zip listings yet—map sync was skipped. Use United States (ZCTA / Census area codes) for groups you need on the map now."
      );
      setSaveSuccessOpen(true);
      setSaving(false);
      setSyncing(false);
      return;
    }

    try {
      const syncFn = httpsCallable(getFunctions(), "syncLocalCarouselToZips");
      const result = await syncFn({ facilityId });
      const d = result.data;
      const syncedCount = d?.syncedZipCount ?? 0;
      const listingStatus = d?.listingStatus || "—";
      const gCount = d?.geographicTargetingGroupCount ?? built.geographicTargetingGroups.length;
      setSaveSuccessMessage(
        `Saved to Firestore and synced ${gCount} targeting group(s) to ${syncedCount} map listing(s) (ZCTA / zip bucket). Listing status: ${listingStatus}. Updates apply for map viewers on their next load.`
      );
      setSaveSuccessOpen(true);
    } catch (e) {
      console.warn("LocalCarouselAdsPage: map sync failed after Firestore save", e);
      const detail = formatLocalCarouselSaveFailureMessage(e, { phase: "sync" });
      const full =
        `Saved to Firestore, but map sync did not complete, so listings may be out of date. ${detail}`;
      setSaveFailureMessage(full);
      setError(full);
      setSaveFailureOpen(true);
    } finally {
      setSaving(false);
      setSyncing(false);
    }
  };

  const handleAdvertiserSaveDraft = async () => {
    if (!isAdvertiserVariant || !mainRef || !uid) {
      return;
    }
    if (reviewStatus === ADVERTISER_CAROUSEL_REVIEW_PENDING) {
      setError("You cannot edit while a submission is pending review.");
      return;
    }
    setSaving(true);
    setError(null);
    setSaveSuccessOpen(false);
    setSaveFailureOpen(false);
    setSaveFailureMessage("");
    const built = buildGeographicTargetingPayload(groups, "advertiser", {
      zipCollections,
      advertiserEditorMode: effectiveEditorMode,
    });
    if (built.error) {
      setError(built.error);
      setSaving(false);
      return;
    }
    try {
      let nextReviewStatus = reviewStatus;
      if (reviewStatus === ADVERTISER_CAROUSEL_REVIEW_APPROVED) {
        nextReviewStatus = ADVERTISER_CAROUSEL_REVIEW_DRAFT;
      }

      await mainRef.set(
        {
          geographicTargetingGroups: built.geographicTargetingGroups,
          reviewStatus: nextReviewStatus,
          updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
          zipCodes: firebase.firestore.FieldValue.delete(),
          slides: firebase.firestore.FieldValue.delete(),
          sponsorDisplayName: firebase.firestore.FieldValue.delete(),
        },
        { merge: true }
      );
      const wasApproved = reviewStatus === ADVERTISER_CAROUSEL_REVIEW_APPROVED;
      setReviewStatus(nextReviewStatus);
      applyPersistedSlidesByGroupId(built.geographicTargetingGroups);
      setSaveSuccessMessage(
        wasApproved
          ? "Draft saved. Submit for review again to update live map listings after approval."
          : "Draft saved. Submit for review when you are ready for an administrator to publish your ads."
      );
      setSaveSuccessOpen(true);
    } catch (e) {
      console.warn("LocalCarouselAdsPage: advertiser draft save failed", e);
      const message = formatLocalCarouselSaveFailureMessage(e, { phase: "firestore" });
      setSaveFailureMessage(message);
      setError(message);
      setSaveFailureOpen(true);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitForReview = async () => {
    if (!isAdvertiserVariant || !uid) return;
    setSubmittingReview(true);
    setError(null);
    setSaveFailureOpen(false);
    setSaveFailureMessage("");
    try {
      const submitFn = httpsCallable(
        getFunctions(),
        "submitAdvertiserCarouselForReview"
      );
      await submitFn({});
      setReviewStatus(ADVERTISER_CAROUSEL_REVIEW_PENDING);
      setSaveSuccessMessage(
        "Submitted for review. An administrator will approve or reject your carousel. You cannot edit until you receive a decision."
      );
      setSaveSuccessOpen(true);
    } catch (e) {
      console.warn("LocalCarouselAdsPage: submit for review failed", e);
      const code = e && typeof e === "object" ? e.code : "";
      const msg =
        e && typeof e === "object" && typeof e.message === "string"
          ? e.message
          : "Submit failed.";
      const detail =
        code === "functions/failed-precondition"
          ? msg
          : `${msg} Save your draft first with at least one valid slide and zip code.`;
      setSaveFailureMessage(detail);
      setError(detail);
      setSaveFailureOpen(true);
    } finally {
      setSubmittingReview(false);
    }
  };

  if (isAdvertiserVariant && !uid) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="warning">Sign in to manage promotional sponsorship ads.</Alert>
      </Container>
    );
  }

  if (!isAdvertiserVariant && !facilityId) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="warning">
          Your account is not linked to a facility (missing <code>facilityId</code>). Local Carousel
          Ads are only available for facility accounts.
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 6, textAlign: "center" }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading Local Carousel Ads…</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
        <Typography variant="h5" component="span">
          {!isAdvertiserVariant
            ? "Local Carousel Ads"
            : effectiveEditorMode === "symptomChecker"
              ? "Symptom Checker sponsorship"
              : effectiveEditorMode === "video"
                ? "Video promotions"
                : effectiveEditorMode === "direct"
                  ? "Direct link promotions"
                  : "Promotional Sponsorship"}
        </Typography>
        {isAdvertiserVariant ? (
          <Chip
            size="small"
            label={ADVERTISER_CAROUSEL_REVIEW_LABELS[reviewStatus] || reviewStatus}
            color={
              reviewStatus === ADVERTISER_CAROUSEL_REVIEW_APPROVED
                ? "success"
                : reviewStatus === ADVERTISER_CAROUSEL_REVIEW_PENDING
                  ? "warning"
                  : reviewStatus === ADVERTISER_CAROUSEL_REVIEW_REJECTED
                    ? "error"
                    : "default"
            }
          />
        ) : null}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {isAdvertiserVariant && effectiveEditorMode === "symptomChecker" ? (
          <>
            Manage the dedicated <strong>Symptom Checker</strong> sponsorship group (US ZCTA codes, sponsor name,
            logo). The standalone Symptom Checker shows <strong>Sponsored by [sponsor name]</strong> below the header;
            CareMap shows the full card. This is the same draft as <strong>Promotions</strong> — save here or there.
          </>
        ) : isAdvertiserVariant && effectiveEditorMode === "video" ? (
          <>
            Edit <strong>YouTube / link-to-video</strong> slides only. Standard website-link slides still
            live in your draft and appear on <strong>Promotions</strong> and <strong>Direct link promotions</strong>.
            Save your draft here or there — it is the same Firestore document.
          </>
        ) : isAdvertiserVariant && effectiveEditorMode === "direct" ? (
          <>
            Edit <strong>standard external-link</strong> slides (CPC) only. Video slides live in the same draft — use{" "}
            <strong>Video promotions</strong> or <strong>Promotions</strong>. Symptom Checker sponsorship has its own
            screen: <Link href="/dashboard/promotional-symptom-checker">Symptom Checker sponsorship</Link>. Submit for
            review from <strong>Promotions</strong> or the Symptom Checker page when you are ready.
          </>
        ) : isAdvertiserVariant ? (
          <>
            Use <strong>geographic targeting groups</strong> to pair US ZCTA / Census area codes or Kenya counties with
            carousel slides (Kenya saves to your draft but is not on the map yet). The same code can appear in
            multiple groups; slides merge on the map in group order.{" "}
            <strong>Symptom Checker</strong> sponsorship is edited on{" "}
            <Link href="/dashboard/promotional-symptom-checker">Symptom Checker sponsorship</Link>. Ads are{" "}
            <strong>not shown</strong> until a system administrator approves your submission. An active{" "}
            <strong>CareMap ad subscription</strong> is still required for live listings. Save your draft,
            then submit for review.
          </>
        ) : (
          <>
            Each <strong>geographic targeting group</strong> chooses a country (United States or Kenya),
            then US ZCTA / Census area codes or Kenya county names, a CareMap audience (patients, clinicians, or
            organizations), max CPC (for standard links) and max CPM (for YouTube video slides), and the
            slides shown there. Overlapping US ZCTAs merge in group order. Kenya county targeting is stored
            with your draft but <strong>does not</strong> sync to map listings yet. Publishing US ZCTAs
            requires an active <strong>CareMap ad subscription</strong> and prepaid balance for
            billable clicks and impressions. Saving updates your draft; Save &amp; sync pushes US zip
            listings when no Kenya-only sync is pending.
          </>
        )}
      </Typography>

      {isAdvertiserVariant && reviewNote && reviewStatus === ADVERTISER_CAROUSEL_REVIEW_REJECTED ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Reviewer note:</strong> {reviewNote}
        </Alert>
      ) : null}

      {isFocusedEditor ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Changes here update the same draft as <strong>Promotions</strong>{" "}
          (/dashboard/promotional-sponsor-carousel).
        </Alert>
      ) : null}

      {!isAdvertiserVariant && facilityId ? (
        <PrepaidCarouselBudgetCard
          ownerKind="facility"
          uid={uid}
          facilityId={facilityId}
          checkoutReturnSegment="facility"
          onCheckoutError={(m) => setError(m)}
          onTopUpReturn={onTopUpReturn}
          balanceRefreshKey={walletRefreshKey}
        />
      ) : null}

      <CareMapAdRateReference
        expanded={healthcareRateRefExpanded}
        onExpandedChange={setHealthcareRateRefExpanded}
      />

      {(!isAdvertiserVariant || effectiveEditorMode === "symptomChecker") ? (
        <Card
          variant="outlined"
          sx={{
            ...contentCardSx,
            mb: 2,
            borderColor: "primary.main",
            borderWidth: 2,
          }}
        >
          <CardContent>
            <Typography variant="subtitle1" gutterBottom fontWeight={600}>
              Symptom Checker sponsorship
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Add a dedicated geographic targeting group with <strong>one</strong> slide that always links to{" "}
              <strong>{CANONICAL_SYMPTOM_CHECKER_PATH}</strong>. For <strong>facilities</strong> and{" "}
              <strong>advertisers</strong>, you supply only a <strong>sponsor name</strong> (institution or brand) and
              an optional <strong>logo</strong>; the standalone Symptom Checker shows{" "}
              <strong>Sponsored by [sponsor name]</strong> in the bar below the header (no button there). CareMap
              still shows the full carousel card: sponsor name line, button label, link, and logo.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={addSymptomCheckerGroup}
              disabled={formLocked || hasSymptomCheckerGtg}
            >
              {hasSymptomCheckerGtg
                ? "Symptom Checker group already added"
                : "Add Symptom Checker targeting group"}
            </Button>
          </CardContent>
        </Card>
      ) : null}

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      {!isAdvertiserVariant ? (
        <Card sx={{ ...contentCardSx, mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              Facility location (shared across groups)
            </Typography>
            <TextField
              fullWidth
              label="Primary location ID (optional)"
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
              helperText="Firestore locations/{id} for map pin and branding when available."
            />
          </CardContent>
        </Card>
      ) : null}

      {groups.map((group, groupIndex) => {
        const isSc = normalizeGtgType(group.gtgType) === GTG_TYPE_SYMPTOM_CHECKER;
        if (isAdvertiserVariant && effectiveEditorMode === "symptomChecker" && !isSc) {
          return null;
        }
        const { hasVideoSlide, hasStandardSlide } = gtgGroupSlideFormatFlags(group, isSc);
        const maxCpcLabel = gtgMaxCpcFieldLabel({ isSc, hasVideoSlide, hasStandardSlide });
        const maxCpmLabel = gtgMaxCpmFieldLabel({ isSc, hasVideoSlide });
        const cpcInputCheck = validateGtgMaxCpcUsdInput(group.maxCpcUsd);
        const cpmInputCheck = validateGtgMaxCpmUsdInput(group.maxCpmUsd);
        const cpcFieldInvalid = (isSc || hasStandardSlide) && !cpcInputCheck.ok;
        const cpmFieldInvalid = !isSc && hasVideoSlide && !cpmInputCheck.ok;
        const rateRefLink = (
          <Link href={`#${CAREMAP_HEALTHCARE_AD_RATE_REFERENCE_ANCHOR_ID}`} underline="hover">
            Healthcare ad rate reference
          </Link>
        );

        if (isAdvertiserVariant && effectiveEditorMode !== "symptomChecker" && isSc) {
          const zc = parseZipTextToUniqueZips(group.zipText || "");
          const row0 = group.slides?.[0];
          return (
            <Card key={group.id} sx={{ ...contentCardSx, mb: 2 }}>
              <CardContent>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Edit Symptom Checker sponsorship on{" "}
                  <Link href="/dashboard/promotional-symptom-checker" underline="hover">
                    Symptom Checker sponsorship
                  </Link>
                  .
                </Alert>
                <Typography variant="subtitle1" gutterBottom>
                  Symptom Checker targeting group {groupIndex + 1}
                </Typography>
                <Chip size="small" color="primary" label="Symptom Checker" variant="outlined" sx={{ mb: 1.5 }} />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  <strong>US zips:</strong> {zc.length ? `${zc.length} configured` : "None"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Sponsor name:</strong>{" "}
                  {row0?.message?.trim()
                    ? plainSponsorNameFromSlideMessage(row0.message) || "—"
                    : "—"}
                </Typography>
              </CardContent>
            </Card>
          );
        }

        const slideIndicesToRender = getSlideIndicesForEditorMode(
          group.slides,
          effectiveEditorMode,
          isSc
        );
        const useSubsetSlideMoves = isFocusedEditor && !isSc;

        return (
        <Card key={group.id} sx={{ ...contentCardSx, mb: 2 }}>
          <CardContent>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
              gap={1}
              sx={{ mb: 1.5 }}
            >
              <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
                <Typography variant="subtitle1">
                  {isSc
                    ? `Symptom Checker targeting group ${groupIndex + 1}`
                    : `Geographic targeting group ${groupIndex + 1}`}
                </Typography>
                {isSc ? (
                  <Chip size="small" color="primary" label="Symptom Checker" variant="outlined" />
                ) : null}
              </Stack>
              <Stack direction="row" alignItems="center" flexWrap="wrap" useFlexGap>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    if (effectiveEditorMode === "video" && !isSc) {
                      addVideoSlide(groupIndex);
                    } else {
                      addSlide(groupIndex);
                    }
                  }}
                  disabled={formLocked || isSc}
                >
                  {effectiveEditorMode === "video" && !isSc
                    ? "Add video slide"
                    : effectiveEditorMode === "direct" && !isSc
                      ? "Add direct link slide"
                      : "Add slide"}
                </Button>
                <IconButton
                  size="small"
                  aria-label="Move group up"
                  disabled={formLocked || groupIndex === 0}
                  onClick={() => moveGroup(groupIndex, -1)}
                >
                  <ArrowUpward fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label="Move group down"
                  disabled={formLocked || groupIndex === groups.length - 1}
                  onClick={() => moveGroup(groupIndex, 1)}
                >
                  <ArrowDownward fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  color="error"
                  aria-label="Remove group"
                  disabled={formLocked || groups.length <= 1}
                  onClick={() => removeGroup(groupIndex)}
                >
                  <DeleteOutline fontSize="small" />
                </IconButton>
              </Stack>
            </Stack>

            {showAdvertiserTargetingCollectionOnly ? (
              <>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Targeting collection
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Choose a collection from{" "}
                  <Link href="/dashboard/promotional-zip-collections">Targeting collections</Link>. Geo
                  targets use the latest saved list whenever you save this draft.
                </Typography>
                <Autocomplete
                  disabled={formLocked}
                  options={zipCollections.filter((c) =>
                    effectiveEditorMode === "symptomChecker" || isSc
                      ? normalizeGtgTargetCountry(c.targetCountry) === GTG_TARGET_COUNTRY_US
                      : true
                  )}
                  getOptionLabel={(opt) => (typeof opt?.name === "string" ? opt.name : "")}
                  isOptionEqualToValue={(a, b) => Boolean(a && b && a.id === b.id)}
                  value={
                    zipCollections.find(
                      (c) => c.id === (group.mapCarouselZipCollectionId || "").trim()
                    ) || null
                  }
                  onChange={(_, v) => applyGroupTargetingCollection(groupIndex, v)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Collection name"
                      placeholder="Select targeting collection…"
                    />
                  )}
                  sx={{ mb: 1 }}
                />
                {zipCollections.length === 0 ? (
                  <Typography variant="caption" color="warning.main" display="block" sx={{ mb: 2 }}>
                    No targeting collections yet. Create one under Targeting collections.
                  </Typography>
                ) : null}
                {(() => {
                  const sel = zipCollections.find(
                    (c) => c.id === (group.mapCarouselZipCollectionId || "").trim()
                  );
                  if (!sel) return null;
                  const tc = normalizeGtgTargetCountry(sel.targetCountry);
                  if (tc === GTG_TARGET_COUNTRY_US) {
                    const n = parseZipTextToUniqueZips(sel.zipText || "").length;
                    return (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                        United States — {n} ZCTA{n === 1 ? "" : "s"} (preview; latest on save)
                      </Typography>
                    );
                  }
                  const sub = parseSubdivisionsForTargetCountry(sel.countyText || "", tc);
                  return (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                      {GTG_TARGET_COUNTRY_LABELS[tc]} — {sub.counties.length}{" "}
                      {gtgSubdivisionPluralNoun(tc)} (preview; latest on save)
                    </Typography>
                  );
                })()}
                {isSc ? (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                    Symptom Checker sponsorship uses United States collections only.
                  </Typography>
                ) : null}
              </>
            ) : (
              <>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Geographic targeting
            </Typography>
            <FormControl fullWidth sx={{ mb: 2 }} disabled={formLocked || isSc}>
              <InputLabel id={`gtg-country-${group.id}`}>Country</InputLabel>
              <Select
                labelId={`gtg-country-${group.id}`}
                label="Country"
                value={
                  isSc
                    ? GTG_TARGET_COUNTRY_US
                    : normalizeGtgTargetCountry(group.targetCountry)
                }
                onChange={(e) =>
                  updateGroupField(groupIndex, "targetCountry", e.target.value)
                }
              >
                <MenuItem value={GTG_TARGET_COUNTRY_US}>
                  {GTG_TARGET_COUNTRY_LABELS[GTG_TARGET_COUNTRY_US]}
                </MenuItem>
                <MenuItem value={GTG_TARGET_COUNTRY_KE}>
                  {GTG_TARGET_COUNTRY_LABELS[GTG_TARGET_COUNTRY_KE]}
                </MenuItem>
                <MenuItem value={GTG_TARGET_COUNTRY_ES}>
                  {GTG_TARGET_COUNTRY_LABELS[GTG_TARGET_COUNTRY_ES]}
                </MenuItem>
                <MenuItem value={GTG_TARGET_COUNTRY_TH}>
                  {GTG_TARGET_COUNTRY_LABELS[GTG_TARGET_COUNTRY_TH]}
                </MenuItem>
              </Select>
            </FormControl>
            {isSc ? (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                Symptom Checker sponsorship uses United States ZCTA / Census area codes only.
              </Typography>
            ) : null}
            {normalizeGtgTargetCountry(group.targetCountry) === GTG_TARGET_COUNTRY_US || isSc ? (
              <>
                <ZipTargetingField
                  value={group.zipText}
                  onChange={(v) => updateGroupField(groupIndex, "zipText", v)}
                  disabled={formLocked}
                  sx={{ mb: 2 }}
                />
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Pick on the map
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <ZipCollectionMapPicker
                    key={group.id}
                    selectedZips={new Set(parseZipTextToUniqueZips(group.zipText || ""))}
                    onZipToggle={(zipId) => toggleGroupZcta(groupIndex, zipId)}
                  />
                </Box>
                {isAdvertiserVariant &&
                zipCollections.some((c) => c.targetCountry === GTG_TARGET_COUNTRY_US) ? (
                  <Stack spacing={1} sx={{ mb: 2 }}>
                    <FormControl size="small" sx={{ maxWidth: 360 }} disabled={formLocked}>
                      <InputLabel id={`zip-coll-${group.id}`}>Saved collection (US ZCTA)</InputLabel>
                      <Select
                        labelId={`zip-coll-${group.id}`}
                        label="Saved collection (US ZCTA)"
                        value={zipApplyPick[group.id] || ""}
                        onChange={(e) =>
                          setZipApplyPick((p) => ({ ...p, [group.id]: String(e.target.value) }))
                        }
                      >
                        <MenuItem value="">
                          <em>Select a saved collection…</em>
                        </MenuItem>
                        {zipCollections
                          .filter((c) => c.targetCountry === GTG_TARGET_COUNTRY_US)
                          .map((c) => (
                            <MenuItem key={c.id} value={c.id}>
                              {c.name} ({parseZipTextToUniqueZips(c.zipText).length} ZCTAs)
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} flexWrap="wrap" useFlexGap>
                      <Tooltip title="Keep your current codes and add every ZCTA from the collection (duplicates removed).">
                        <span>
                          <Button
                            variant="outlined"
                            disabled={formLocked}
                            onClick={() => applyZipCollectionForGroup(groupIndex, "merge")}
                          >
                            Merge collection
                          </Button>
                        </span>
                      </Tooltip>
                      <Tooltip title="Replace this group's entire ZCTA list with the collection only.">
                        <span>
                          <Button
                            variant="outlined"
                            color="secondary"
                            disabled={formLocked}
                            onClick={() => applyZipCollectionForGroup(groupIndex, "replace")}
                          >
                            Replace with collection
                          </Button>
                        </span>
                      </Tooltip>
                    </Stack>
                  </Stack>
                ) : null}
              </>
            ) : normalizeGtgTargetCountry(group.targetCountry) === GTG_TARGET_COUNTRY_KE ? (
              <>
                <CountyTargetingField
                  value={group.countyText || ""}
                  onChange={(v) => updateGroupField(groupIndex, "countyText", v)}
                  disabled={formLocked}
                  sx={{ mb: 1 }}
                />
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                  County names match Google Ads location names (County, Kenya) and the{" "}
                  <Link href={`#${CAREMAP_HEALTHCARE_AD_RATE_REFERENCE_ANCHOR_ID}`} underline="hover">
                    Healthcare ad rate reference
                  </Link>
                  .
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <KenyaCountyMapPicker
                    key={`ke-map-${group.id}`}
                    selectedCounties={
                      new Set(parseKenyaCountiesText(group.countyText || "").counties)
                    }
                    onCountyToggle={(canon) => toggleGroupSubdivision(groupIndex, canon)}
                    disabled={formLocked}
                  />
                </Box>
                <Divider sx={{ my: 2 }} />
                <KenyaCountyChecklistPicker
                  value={group.countyText || ""}
                  onChange={(v) => updateGroupField(groupIndex, "countyText", v)}
                  disabled={formLocked}
                />
                {isAdvertiserVariant &&
                zipCollections.some(
                  (c) =>
                    normalizeGtgTargetCountry(c.targetCountry) === GTG_TARGET_COUNTRY_KE
                ) ? (
                  <Stack spacing={1} sx={{ mb: 2 }}>
                    <FormControl size="small" sx={{ maxWidth: 360 }} disabled={formLocked}>
                      <InputLabel id={`county-coll-${group.id}`}>Saved collection (Kenya)</InputLabel>
                      <Select
                        labelId={`county-coll-${group.id}`}
                        label="Saved collection (Kenya)"
                        value={zipApplyPick[group.id] || ""}
                        onChange={(e) =>
                          setZipApplyPick((p) => ({ ...p, [group.id]: String(e.target.value) }))
                        }
                      >
                        <MenuItem value="">
                          <em>Select a saved collection…</em>
                        </MenuItem>
                        {zipCollections
                          .filter((c) => normalizeGtgTargetCountry(c.targetCountry) === GTG_TARGET_COUNTRY_KE)
                          .map((c) => (
                            <MenuItem key={c.id} value={c.id}>
                              {c.name} (
                              {parseKenyaCountiesText(c.countyText || "").counties.length} counties)
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} flexWrap="wrap" useFlexGap>
                      <Tooltip title="Keep your current counties and add every county from the collection (duplicates removed).">
                        <span>
                          <Button
                            variant="outlined"
                            disabled={formLocked}
                            onClick={() => applyZipCollectionForGroup(groupIndex, "merge")}
                          >
                            Merge collection
                          </Button>
                        </span>
                      </Tooltip>
                      <Tooltip title="Replace this group's entire county list with the collection only.">
                        <span>
                          <Button
                            variant="outlined"
                            color="secondary"
                            disabled={formLocked}
                            onClick={() => applyZipCollectionForGroup(groupIndex, "replace")}
                          >
                            Replace with collection
                          </Button>
                        </span>
                      </Tooltip>
                    </Stack>
                  </Stack>
                ) : null}
              </>
            ) : (
              <>
                <GtgProvinceTargetingField
                  targetCountry={normalizeGtgTargetCountry(group.targetCountry)}
                  value={group.countyText || ""}
                  onChange={(v) => updateGroupField(groupIndex, "countyText", v)}
                  disabled={formLocked}
                  sx={{ mb: 1 }}
                />
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Pick on the map
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <GtgProvinceMapPicker
                    key={`gtg-prov-${group.id}-${normalizeGtgTargetCountry(group.targetCountry)}`}
                    targetCountry={normalizeGtgTargetCountry(group.targetCountry)}
                    selectedProvinces={
                      new Set(
                        parseSubdivisionsForTargetCountry(
                          group.countyText || "",
                          normalizeGtgTargetCountry(group.targetCountry)
                        ).counties
                      )
                    }
                    onProvinceToggle={(canon) => toggleGroupSubdivision(groupIndex, canon)}
                    disabled={formLocked}
                  />
                </Box>
                <Divider sx={{ my: 2 }} />
                <GtgSubdivisionChecklistPicker
                  targetCountry={normalizeGtgTargetCountry(group.targetCountry)}
                  value={group.countyText || ""}
                  onChange={(v) => updateGroupField(groupIndex, "countyText", v)}
                  disabled={formLocked}
                />
                {isAdvertiserVariant &&
                zipCollections.some(
                  (c) =>
                    normalizeGtgTargetCountry(c.targetCountry) ===
                    normalizeGtgTargetCountry(group.targetCountry)
                ) ? (
                  <Stack spacing={1} sx={{ mb: 2 }}>
                    <FormControl size="small" sx={{ maxWidth: 360 }} disabled={formLocked}>
                      <InputLabel id={`subdiv-coll-${group.id}`}>
                        Saved collection (
                        {GTG_TARGET_COUNTRY_LABELS[normalizeGtgTargetCountry(group.targetCountry)]})
                      </InputLabel>
                      <Select
                        labelId={`subdiv-coll-${group.id}`}
                        label={`Saved collection (${GTG_TARGET_COUNTRY_LABELS[normalizeGtgTargetCountry(group.targetCountry)]})`}
                        value={zipApplyPick[group.id] || ""}
                        onChange={(e) =>
                          setZipApplyPick((p) => ({ ...p, [group.id]: String(e.target.value) }))
                        }
                      >
                        <MenuItem value="">
                          <em>Select a saved collection…</em>
                        </MenuItem>
                        {zipCollections
                          .filter(
                            (c) =>
                              normalizeGtgTargetCountry(c.targetCountry) ===
                              normalizeGtgTargetCountry(group.targetCountry)
                          )
                          .map((c) => (
                            <MenuItem key={c.id} value={c.id}>
                              {c.name} (
                              {
                                parseSubdivisionsForTargetCountry(
                                  c.countyText || "",
                                  normalizeGtgTargetCountry(group.targetCountry)
                                ).counties.length
                              }{" "}
                              {gtgSubdivisionPluralNoun(normalizeGtgTargetCountry(group.targetCountry))})
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} flexWrap="wrap" useFlexGap>
                      <Tooltip title="Keep your current list and add every subdivision from the collection (duplicates removed).">
                        <span>
                          <Button
                            variant="outlined"
                            disabled={formLocked}
                            onClick={() => applyZipCollectionForGroup(groupIndex, "merge")}
                          >
                            Merge collection
                          </Button>
                        </span>
                      </Tooltip>
                      <Tooltip title="Replace this group's entire subdivision list with the collection only.">
                        <span>
                          <Button
                            variant="outlined"
                            color="secondary"
                            disabled={formLocked}
                            onClick={() => applyZipCollectionForGroup(groupIndex, "replace")}
                          >
                            Replace with collection
                          </Button>
                        </span>
                      </Tooltip>
                    </Stack>
                  </Stack>
                ) : null}
              </>
            )}
            </>
            )}
            <TextField
              fullWidth
              label={
                isAdvertiserVariant
                  ? "Sponsor display name (optional)"
                  : "Display name (optional)"
              }
              value={group.displayName}
              onChange={(e) => updateGroupField(groupIndex, "displayName", e.target.value)}
              helperText={
                isSc
                  ? "Optional CareMap card title only. The Symptom Checker sponsorship line uses the slide “Sponsor name” field below, not this line."
                  : isAdvertiserVariant
                    ? "Shown as the carousel title when your slides appear (defaults to “Promotional sponsor”)."
                    : "Shown as the carousel title for this group when set; otherwise uses your primary location name if available."
              }
              disabled={formLocked}
              sx={{ mb: 2 }}
            />

            <FormControl fullWidth sx={{ mb: 2 }} disabled={formLocked}>
              <InputLabel id={`gtg-audience-${groupIndex}`}>CareMap audience</InputLabel>
              <Select
                labelId={`gtg-audience-${groupIndex}`}
                label="CareMap audience"
                value={group.targetAudience || "patient"}
                onChange={(e) =>
                  updateGroupField(groupIndex, "targetAudience", e.target.value)
                }
              >
                {GTG_TARGET_AUDIENCES.map((v) => (
                  <MenuItem key={v} value={v}>
                    {GTG_TARGET_AUDIENCE_LABELS[v]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              type="number"
              label={maxCpcLabel}
              value={group.maxCpcUsd ?? MIN_GTG_CPC_USD}
              error={cpcFieldInvalid}
              onChange={(e) => {
                const n = parseFloat(e.target.value);
                updateGroupField(
                  groupIndex,
                  "maxCpcUsd",
                  Number.isFinite(n) ? n : MIN_GTG_CPC_USD
                );
              }}
              inputProps={{
                min: MIN_GTG_CPC_USD,
                max: MAX_GTG_CPC_USD,
                step: 0.01,
              }}
              helperText={
                cpcFieldInvalid ? (
                  <>
                    {cpcInputCheck.error} See {rateRefLink}.
                  </>
                ) : (
                  <>
                    Floor ${MIN_GTG_CPC_USD} — cap ${MAX_GTG_CPC_USD} (see {rateRefLink}). Used for
                    standard link slides: orders priority and sets the debit per eligible click.
                  </>
                )
              }
              disabled={formLocked}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              type="number"
              label={maxCpmLabel}
              value={group.maxCpmUsd ?? MIN_GTG_CPM_USD}
              error={cpmFieldInvalid}
              onChange={(e) => {
                const n = parseFloat(e.target.value);
                updateGroupField(
                  groupIndex,
                  "maxCpmUsd",
                  Number.isFinite(n) ? n : MIN_GTG_CPM_USD
                );
              }}
              inputProps={{
                min: MIN_GTG_CPM_USD,
                max: MAX_GTG_CPM_USD,
                step: 0.01,
              }}
              helperText={
                cpmFieldInvalid ? (
                  <>
                    {cpmInputCheck.error} See {rateRefLink}.
                  </>
                ) : (
                  <>
                    Floor ${MIN_GTG_CPM_USD} — cap ${MAX_GTG_CPM_USD} (see {rateRefLink}). Required when
                    any slide uses “Link to video”: each carousel impression deducts roughly (CPM ÷ 1,000)
                    in USD from prepay.
                  </>
                )
              }
              disabled={formLocked}
              sx={{ mb: 2 }}
            />

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Slides (order = carousel order)
            </Typography>

            {isFocusedEditor && !isSc && slideIndicesToRender.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {effectiveEditorMode === "video"
                  ? "No video slides in this group yet. Add one above."
                  : "No direct-link slides in this group yet. Add one above."}
              </Typography>
            ) : null}

            {slideIndicesToRender.map((slideIndex, displayIdx) => {
              const row = group.slides[slideIndex];
              const effectiveVideo =
                !isSc &&
                (effectiveEditorMode === "video" ||
                  (effectiveEditorMode === "full" && row.linkToVideo));
              const showLinkTypeRadio = !isSc && !isFocusedEditor;
              return (
              <Card
                key={row.id || `slide-${groupIndex}-${slideIndex}`}
                variant="outlined"
                sx={{ mb: 1.5, bgcolor: "action.hover" }}
              >
                <CardContent>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 1 }}
                  >
                    <Box>
                      <Typography variant="subtitle2">
                        {isSc
                          ? "Symptom Checker slide"
                          : isFocusedEditor && !isSc
                            ? effectiveEditorMode === "video"
                              ? `Video slide ${displayIdx + 1}`
                              : `Direct link slide ${displayIdx + 1}`
                            : `Slide ${slideIndex + 1}`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        ID: {row.id || "—"}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ mt: 0.25 }}
                      >
                        {isValidMapCarouselSlideId(
                          typeof row.id === "string" ? row.id.trim() : ""
                        )
                          ? formatCarouselStatsLine(statsBySlideId[row.id.trim()])
                          : "Metrics use the same system as global carousel ads. Slide id must be 8–128 characters (letters, numbers, _ or -)."}
                      </Typography>
                    </Box>
                    <Stack direction="row" alignItems="center">
                      <FormControlLabelCompact
                        control={
                          <Switch
                            checked={row.enabled !== false}
                            disabled={formLocked}
                            onChange={(e) =>
                              updateSlide(groupIndex, slideIndex, "enabled", e.target.checked)
                            }
                          />
                        }
                        label="Enabled"
                      />
                      <IconButton
                        size="small"
                        aria-label="Move slide up"
                        disabled={
                          formLocked ||
                          isSc ||
                          (useSubsetSlideMoves
                            ? displayIdx === 0
                            : slideIndex === 0)
                        }
                        onClick={() =>
                          useSubsetSlideMoves
                            ? moveSlideForEditorMode(groupIndex, displayIdx, -1)
                            : moveSlide(groupIndex, slideIndex, -1)
                        }
                      >
                        <ArrowUpward fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label="Move slide down"
                        disabled={
                          formLocked ||
                          isSc ||
                          (useSubsetSlideMoves
                            ? displayIdx === slideIndicesToRender.length - 1
                            : slideIndex === group.slides.length - 1)
                        }
                        onClick={() =>
                          useSubsetSlideMoves
                            ? moveSlideForEditorMode(groupIndex, displayIdx, 1)
                            : moveSlide(groupIndex, slideIndex, 1)
                        }
                      >
                        <ArrowDownward fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        aria-label="Remove slide"
                        disabled={formLocked || (isSc && group.slides.length <= 1)}
                        onClick={() => removeSlide(groupIndex, slideIndex)}
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Stack>
                  <FormControl
                    size="small"
                    sx={{ minWidth: 220, mb: 1.5 }}
                    disabled={formLocked}
                  >
                    <InputLabel id={`lc-gender-${groupIndex}-${slideIndex}`}>
                      Patient audience
                    </InputLabel>
                    <Select
                      labelId={`lc-gender-${groupIndex}-${slideIndex}`}
                      label="Patient audience"
                      value={normalizeLocalCarouselGender(row.patientSegments?.gender)}
                      onChange={(e) =>
                        updateSlide(groupIndex, slideIndex, "patientGender", e.target.value)
                      }
                    >
                      {LOCAL_CAROUSEL_GENDERS.map((g) => (
                        <MenuItem key={g} value={g}>
                          {LOCAL_CAROUSEL_GENDER_LABELS[g]}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    fullWidth
                    multiline={!isSc}
                    minRows={isSc ? 1 : 2}
                    label={
                      isSc
                        ? "Sponsor name (institution or brand)"
                        : "Message (HTML allowed)"
                    }
                    value={row.message}
                    onChange={(e) =>
                      updateSlide(groupIndex, slideIndex, "message", e.target.value)
                    }
                    sx={{ mb: isSc ? 0.5 : 1.5 }}
                    disabled={formLocked}
                    helperText={
                      isSc
                        ? "Plain text recommended. Shown on /symptomchecker as “Sponsored by [name]”. The same value is the message line on CareMap (HTML works on CareMap only; tags are stripped for the Symptom Checker line)."
                        : undefined
                    }
                  />
                  {isSc ? (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mb: 1.5 }}
                    >
                      Symptom Checker preview: Sponsored by{" "}
                      <strong>
                        {row.message?.trim()
                          ? plainSponsorNameFromSlideMessage(row.message) || "…"
                          : "…"}
                      </strong>
                    </Typography>
                  ) : null}
                  <TextField
                    fullWidth
                    label="Button label (shown on CareMap only)"
                    value={row.buttonLabel}
                    onChange={(e) =>
                      updateSlide(groupIndex, slideIndex, "buttonLabel", e.target.value)
                    }
                    sx={{ mb: 1.5 }}
                    disabled={formLocked}
                    helperText={
                      isSc
                        ? "Not shown on the standalone Symptom Check page — only on CareMap."
                        : undefined
                    }
                  />
                  {!isSc ? (
                    <>
                      {showLinkTypeRadio ? (
                        <FormControl
                          component="fieldset"
                          variant="standard"
                          sx={{ mb: 1.5, width: "100%" }}
                        >
                          <Typography component="legend" variant="caption" color="text.secondary">
                            Link type
                          </Typography>
                          <RadioGroup
                            row
                            value={row.linkToVideo ? "video" : "link"}
                            onChange={(e) => {
                              const toVideo = e.target.value === "video";
                              updateSlide(groupIndex, slideIndex, "linkToVideo", toVideo);
                            }}
                          >
                            <FormControlLabel
                              value="link"
                              control={<Radio size="small" />}
                              label={
                                <GtgCpcRadioLabel
                                  disabled={formLocked}
                                  onInfoClick={() => setPricingInfoModal("cpc")}
                                />
                              }
                              disabled={formLocked}
                            />
                            <FormControlLabel
                              value="video"
                              control={<Radio size="small" />}
                              label={
                                <GtgCpmRadioLabel
                                  disabled={formLocked}
                                  onInfoClick={() => setPricingInfoModal("cpm")}
                                />
                              }
                              disabled={formLocked}
                            />
                          </RadioGroup>
                        </FormControl>
                      ) : null}
                      {effectiveVideo ? (
                        <>
                          <Alert severity="warning" sx={{ mb: 1 }}>
                            <strong>CPM — not CPC:</strong> With “Link to video” selected, your prepaid
                            balance is charged each time this slide is <strong>shown</strong> in the CareMap
                            carousel (per the max CPM you set above), not when someone taps the button.
                            Only <strong>YouTube</strong> URLs are allowed in the main link field.
                          </Alert>
                          <Alert severity="info" variant="outlined" sx={{ mb: 1.5 }}>
                            <GtgYoutubeLinkInstructions />
                          </Alert>
                        </>
                      ) : null}
                    </>
                  ) : null}
                  <TextField
                    fullWidth
                    label={
                      isSc
                        ? "Link (fixed)"
                        : effectiveVideo
                          ? "YouTube video URL (watch, shorts, or youtu.be)"
                          : "Link (https://, mailto:, or path starting with /)"
                    }
                    value={isSc ? CANONICAL_SYMPTOM_CHECKER_PATH : row.href}
                    onChange={(e) =>
                      updateSlide(groupIndex, slideIndex, "href", e.target.value)
                    }
                    sx={{ mb: 1.5 }}
                    disabled={formLocked || isSc}
                    helperText={
                      isSc
                        ? "Fixed for Symptom Checker sponsorship. CareMap still uses the button label above."
                        : effectiveVideo
                          ? "Use a full YouTube video URL (see the note above for accepted formats). Other hosts are not supported. This mode bills CPM on carousel impressions."
                          : "YouTube links must use “Link to video (CPM)”. Standard links bill CPC on click."
                    }
                    InputProps={{
                      endAdornment: !isSc ? (
                        <InputAdornment position="end">
                          <Tooltip title="Open link in a new tab to verify it works">
                            <span>
                              <IconButton
                                edge="end"
                                aria-label={`Test slide ${slideIndex + 1} link in new tab`}
                                onClick={() => {
                                  setError(null);
                                  const ok = openCarouselHrefInNewTab(row.href);
                                  if (!ok) {
                                    setError(
                                      "Use https://, mailto:, or an in-app path starting with / before testing."
                                    );
                                  }
                                }}
                              >
                                <OpenInNew fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </InputAdornment>
                      ) : undefined,
                    }}
                  />
                  {!isSc && effectiveVideo ? (
                    <TextField
                      fullWidth
                      label="Learn more link (optional, no extra charge)"
                      value={row.learnMoreHref || ""}
                      onChange={(e) =>
                        updateSlide(groupIndex, slideIndex, "learnMoreHref", e.target.value)
                      }
                      sx={{ mb: 1.5 }}
                      disabled={formLocked}
                      helperText="Optional second URL opened in a new tab (CPC does not apply). Use https://, mailto:, or an in-app path starting with /."
                    />
                  ) : null}
                  <TextField
                    fullWidth
                    label="Logo URL (optional)"
                    value={row.logoUrl}
                    onChange={(e) =>
                      updateSlide(groupIndex, slideIndex, "logoUrl", e.target.value)
                    }
                    helperText={MAP_CAROUSEL_LOGO_URL_HELPER_TEXT}
                    disabled={formLocked}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <CarouselLogoSlotPreviewButton
                              logoUrl={row.logoUrl}
                              ariaLabel={`Preview slide ${slideIndex + 1} logo in CareMap slot`}
                              onInvalidUrl={() =>
                                setError(
                                  "Use https:// or an in-app path starting with / (not mailto:) before previewing the logo."
                                )
                              }
                            />
                            <Tooltip title="Open logo URL in a new tab to verify it loads">
                              <span>
                                <IconButton
                                  edge="end"
                                  aria-label={`Test slide ${slideIndex + 1} logo URL in new tab`}
                                  onClick={() => {
                                    setError(null);
                                    const ok = openCarouselHrefInNewTab(row.logoUrl);
                                    if (!ok) {
                                      setError(
                                        "Use https://, mailto:, or an in-app path starting with / before testing the logo URL."
                                      );
                                    }
                                  }}
                                >
                                  <OpenInNew fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Stack>
                        </InputAdornment>
                      ),
                    }}
                  />
                </CardContent>
              </Card>
              );
            })}
          </CardContent>
        </Card>
        );
      })}

      {!(isAdvertiserVariant && effectiveEditorMode === "symptomChecker") ? (
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={addGroup}
          disabled={formLocked}
          sx={{ mb: 2 }}
        >
          Add geographic targeting group
        </Button>
      ) : null}

      <Divider sx={{ my: 2 }} />

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {isAdvertiserVariant ? (
          <>
            <LoadingButton
              variant="contained"
              startIcon={<SaveIcon />}
              loading={saving}
              onClick={handleAdvertiserSaveDraft}
              disabled={formLocked}
            >
              Save draft
            </LoadingButton>
            <LoadingButton
              variant="contained"
              color="secondary"
              loading={submittingReview}
              onClick={handleSubmitForReview}
              disabled={
                formLocked ||
                (reviewStatus !== ADVERTISER_CAROUSEL_REVIEW_DRAFT &&
                  reviewStatus !== ADVERTISER_CAROUSEL_REVIEW_REJECTED)
              }
            >
              Submit for review
            </LoadingButton>
          </>
        ) : (
          <LoadingButton
            variant="contained"
            startIcon={<SaveIcon />}
            loading={saving || syncing}
            onClick={handleSaveAndSync}
          >
            Save & sync to map
          </LoadingButton>
        )}
        <Button variant="outlined" onClick={loadDoc}>
          Reload from server
        </Button>
      </Stack>

      <Snackbar
        open={saveSuccessOpen}
        autoHideDuration={SAVE_SUCCESS_SNACKBAR_MS}
        onClose={(_, reason) => {
          if (reason === "clickaway") return;
          setSaveSuccessOpen(false);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setSaveSuccessOpen(false)}
          sx={{ width: "100%" }}
        >
          {saveSuccessMessage ||
            "Saved to Firestore and synced to map listings (ZCTA / zip bucket). Updates apply for map viewers on their next load."}
        </Alert>
      </Snackbar>

      <Snackbar
        open={saveFailureOpen}
        autoHideDuration={SAVE_FAILURE_SNACKBAR_MS}
        onClose={(_, reason) => {
          if (reason === "clickaway") return;
          setSaveFailureOpen(false);
          setSaveFailureMessage("");
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={() => {
            setSaveFailureOpen(false);
            setSaveFailureMessage("");
          }}
          sx={{ width: "100%" }}
        >
          {saveFailureMessage ||
            "Save or sync failed. Your changes may not be on the map yet. Check your connection and use Reload from server."}
        </Alert>
      </Snackbar>

      <Dialog
        open={pricingInfoModal != null}
        onClose={() => setPricingInfoModal(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {pricingInfoModal === "cpc"
            ? "Cost per click (CPC)"
            : pricingInfoModal === "cpm"
              ? "Cost per mille (CPM)"
              : ""}
        </DialogTitle>
        <DialogContent dividers>
          {pricingInfoModal === "cpc" ? (
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                <strong>Cost per click (CPC)</strong> is what you pay (or cap) for each{" "}
                <strong>click</strong> on your carousel link. The prepaid wallet is debited when someone follows the
                link—not merely when the card appears in the carousel.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Compared to CPM:</strong> <strong>Cost per mille (CPM)</strong> prices{" "}
                <strong>one thousand impressions</strong>—each time your creative is <strong>shown</strong>, whether or
                not anyone clicks. On CareMap, <strong>Link to video</strong> slides use CPM-style billing: charges accrue
                when that slide is displayed in the carousel (scaled by your max CPM), not when the user opens the video.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>On this page:</strong> choose <strong>Standard link (CPC)</strong> for ordinary URLs; use{" "}
                <strong>Link to video (CPM)</strong> only for YouTube URLs that should bill on impressions.
              </Typography>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => {
                  setPricingInfoModal(null);
                  scrollToHealthcareAdRateReference();
                }}
                sx={{ cursor: "pointer", alignSelf: "flex-start" }}
              >
                Open Healthcare ad rate reference on this page (expanded)
              </Link>
            </Stack>
          ) : pricingInfoModal === "cpm" ? (
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                <strong>Cost per mille (CPM)</strong> (“mille” = thousand) is what you pay (or cap) per{" "}
                <strong>1,000 impressions</strong>—each time your ad is <strong>served or shown</strong>, not per click.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Compared to CPC:</strong> With <strong>cost per click</strong>, charges tie to{" "}
                <strong>clicks</strong> only. CPM spreads cost across many views; you may pay for visibility even if nobody
                clicks. That can suit video or brand reach; CPC often suits direct response links.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>On this page:</strong> <strong>Link to video (CPM)</strong> uses CPM-style debits when the YouTube
                slide appears in the CareMap carousel. <strong>Standard link (CPC)</strong> continues to bill on click.
              </Typography>
              <GtgYoutubeLinkInstructions title="YouTube links (required for this mode)" />
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => {
                  setPricingInfoModal(null);
                  scrollToHealthcareAdRateReference();
                }}
                sx={{ cursor: "pointer", alignSelf: "flex-start" }}
              >
                Open Healthcare ad rate reference on this page (expanded)
              </Link>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPricingInfoModal(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

/** Renders GTG_YOUTUBE_LINK_HELP_LINES (shared-utils; matches extractYoutubeVideoId). */
function GtgYoutubeLinkInstructions({ variant = "body2", title = "Linking to YouTube" }) {
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.75 }}>
        {title}
      </Typography>
      <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2.25 }}>
        {GTG_YOUTUBE_LINK_HELP_LINES.map((line) => (
          <Typography
            key={line}
            component="li"
            variant={variant}
            color="text.secondary"
            sx={{ lineHeight: 1.55 }}
          >
            {line}
          </Typography>
        ))}
      </Stack>
    </Box>
  );
}

/** Radio row label: “Standard link (CPC)” + info (does not change radio when icon is used). */
function GtgCpcRadioLabel({ onInfoClick, disabled }) {
  return (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}>
      <span>Standard link (CPC)</span>
      <Tooltip title="What is CPC?">
        <span>
          <IconButton
            size="small"
            aria-label="Explain cost per click (CPC)"
            disabled={disabled}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onInfoClick();
            }}
            sx={GTG_INFO_ICON_BUTTON_SX}
          >
            <InfoOutlined sx={{ fontSize: 15 }} />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
}

/** Radio row label: “Link to video (CPM)” + info. */
function GtgCpmRadioLabel({ onInfoClick, disabled }) {
  return (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}>
      <span>Link to video (CPM)</span>
      <Tooltip title="What is CPM?">
        <span>
          <IconButton
            size="small"
            aria-label="Explain cost per mille (CPM)"
            disabled={disabled}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onInfoClick();
            }}
            sx={GTG_INFO_ICON_BUTTON_SX}
          >
            <InfoOutlined sx={{ fontSize: 15 }} />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
}

/** MUI FormControlLabel pulls in extra bundle; compact inline label for Switch */
function FormControlLabelCompact({ control, label }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", mr: 1 }}>
      {control}
      <Typography component="span" variant="body2" sx={{ ml: 0.5 }}>
        {label}
      </Typography>
    </Box>
  );
}

export default LocalCarouselAdsPage;
